/**
 * BottomSheet Pattern
 *
 * Mobile-first bottom sheet that slides up from the bottom of the screen.
 * Built on Radix Dialog for accessibility (focus trap, aria, scroll lock).
 *
 * Uses inline styles for overlay + slide animation so it works in any
 * Tailwind config (no tailwindcss-animate or data-attribute variants needed).
 *
 * Mirrors the SimpleCRUDDrawer API:
 * - open / onOpenChange for state
 * - title (header)
 * - children (scrollable content)
 * - footer (sticky actions)
 * - isBusy to block close during async work
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { BottomSheetProps } from './types'

export function BottomSheet({
  open,
  onOpenChange,
  title,
  ariaLabel,
  description,
  children,
  footer,
  isBusy = false,
  className,
  maxHeight = '85vh',
  height,
}: BottomSheetProps) {
  // `mounted` keeps Radix DOM alive during exit animation.
  // `visible` drives the CSS transition (false = off-screen, true = visible).
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const rafRef = useRef<number | undefined>(undefined)
  const dragStartY = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (isBusy && !next) return
      onOpenChange(next)
    },
    [isBusy, onOpenChange],
  )

  useEffect(() => {
    clearTimeout(timerRef.current)
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current)

    if (open) {
      // Mount first, then animate in on the next paint
      setMounted(true)
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setVisible(true))
      })
    } else {
      // Animate out, then unmount after transition completes
      setDragOffset(0)
      dragOffsetRef.current = 0
      setVisible(false)
      timerRef.current = setTimeout(() => setMounted(false), 300)
    }

    return () => {
      clearTimeout(timerRef.current)
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current)
    }
  }, [open])

  const handleDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isBusy) return
    dragStartY.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [isBusy])

  const handleDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return
    const offset = Math.max(0, e.clientY - dragStartY.current)
    dragOffsetRef.current = offset
    setDragOffset(offset)
  }, [])

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null) return
    const delta = dragOffsetRef.current
    dragStartY.current = null
    dragOffsetRef.current = 0
    if (delta > 80) {
      handleOpenChange(false)
    } else {
      setDragOffset(0)
    }
  }, [handleOpenChange])

  const isDragging = dragOffset > 0

  return (
    <DialogPrimitive.Root open={mounted} onOpenChange={handleOpenChange}>
      {mounted && (
        <DialogPrimitive.Portal forceMount>
          {/* Overlay — onClick fixes iOS WKWebView not firing onInteractOutside */}
          <DialogPrimitive.Overlay
            onClick={() => handleOpenChange(false)}
            className="fixed inset-0 z-50 bg-butter-gray-900/15 transition-opacity duration-300 ease-in-out"
            style={{ opacity: visible ? 1 : 0 }}
          />

          {/* Sheet panel */}
          <DialogPrimitive.Content
            {...(!description && { 'aria-describedby': undefined })}
            onInteractOutside={(e) => { e.preventDefault() }}
            onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault() }}
            className={`fixed bottom-0 left-0 right-0 z-50 flex flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_24px_rgba(0,0,0,0.12)] ${className ?? ''}`}
            style={{
              maxHeight: height ?? maxHeight,
              height: height,
              transform: visible ? `translateY(${dragOffset}px)` : 'translateY(100%)',
              transition: isDragging ? 'none' : 'transform 300ms ease-out',
            }}
          >
            {/* Drag handle — touch target for swipe-to-close */}
            <div
              className="flex min-h-[44px] items-center justify-center touch-none"
              onPointerDown={handleDragStart}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
            >
              <div className="h-1 w-10 rounded-full bg-butter-gray-300" />
            </div>

            {/* Header */}
            {title ? (
              <div className="px-5 pb-6 pt-4">
                <DialogPrimitive.Title className="text-lg font-semibold leading-tight text-butter-gray-900">
                  {title}
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description
                    data-testid="bottom-sheet-description"
                    className="mt-1 text-base font-medium text-butter-gray-600"
                  >
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>
            ) : (
              <DialogPrimitive.Title className="sr-only">
                {ariaLabel ?? 'Dialog'}
              </DialogPrimitive.Title>
            )}

            {/* Scrollable content */}
            <div className={`flex-1 overflow-y-auto px-5 pb-4 ${title ? '' : 'pt-5'}`}>
              {children}
            </div>

            {/* Sticky footer */}
            {footer && (
              <div className="px-5 pb-2 pt-4">
                {footer}
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      )}
    </DialogPrimitive.Root>
  )
}
